import React, { useState } from "react";
import { MarketingTick } from "../components/MarketingConsent";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { ShoppingBag } from "lucide-react";
import { GoogleButton } from "../components/GoogleSignIn";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api, AppConfig, errorMessage } from "../lib/api";
import { safeNext } from "../lib/links";
import { HowItWorksStrip } from "../components/OptionExplainer";

const HERO = "https://images.pexels.com/photos/5490196/pexels-photo-5490196.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<"login" | "register" | "forgot">(params.get("forgot") ? "forgot" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const next = safeNext(params.get("next"));
  const ssoError = params.get("sso_error");
  const { data: config } = useQuery<AppConfig>({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data), staleTime: 300000 });
  const shopifyStart = `${process.env.REACT_APP_BACKEND_URL}/api/auth/shopify/start?next=${encodeURIComponent(next || "/")}`;
  if (user) return <Navigate to={next || (typeof user === "object" && user.role === "admin" ? "/admin" : "/")} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else if (mode === "forgot") { await api.post("/auth/forgot-password", { email }); setSent(true); }
      else await register(email, password, name, "", marketing);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const titles = { login: "Enter the cellar", register: "Open your cellar", forgot: "Forgotten your password?" };
  const intros = { login: "Sign in to view your balance, plan and orders.", register: "Create an account to start building cellar credit.", forgot: "Tell us your email and we'll send a link to choose a new one. The link works for one hour." };

  return (
    <div className="grain grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src={HERO} alt="Sommelier pouring wine" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/90 via-[var(--ink)]/40 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16 text-[var(--paper)]">
          <div className="eyebrow !text-[var(--paper)]/70">Hidden France</div>
          <h2 className="mt-3 font-serif text-4xl font-medium leading-tight lg:text-5xl">Wines you won’t find anywhere else in the UK — paid from your own cellar.</h2>
          <p className="mt-4 max-w-md text-sm text-[var(--paper)]/80">Top up by Direct Debit, monthly or whenever you like. Draw down bottle by bottle.</p>
        </div>
      </div>
      <div className="flex items-center px-8 py-16 lg:px-24">
        <form onSubmit={submit} className="w-full max-w-md rise" data-testid="auth-form">
          <img src="/brand/logo.png" alt="Pierre Hourlier Wines" className="h-24 w-auto" data-testid="auth-logo" />
          <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">{titles[mode]}</h1>
          <p className="mt-3 text-sm text-neutral-600">{intros[mode]}</p>
          {mode === "forgot" && sent ? (
            <div className="mt-10 border border-border p-6" data-testid="forgot-sent">
              <p className="font-serif text-2xl">Check your inbox</p>
              <p className="mt-2 text-sm text-neutral-600">If <strong>{email}</strong> has an account, a reset link is on its way. It's valid for one hour — check your spam folder if it doesn't arrive.</p>
              <Button type="button" variant="outline" className="mt-6 rounded-none" onClick={() => { setMode("login"); setSent(false); }} data-testid="forgot-back-to-login">Back to sign in</Button>
            </div>
          ) : (
          <div className="mt-10 space-y-6">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="eyebrow">Full name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none bg-transparent" data-testid="register-name-input" />
                <MarketingTick checked={marketing} onChange={setMarketing} testId="register-marketing-tick" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none bg-transparent" data-testid="auth-email-input" />
            </div>
            {mode !== "forgot" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="eyebrow">Password</Label>
                {mode === "login" && <button type="button" onClick={() => setMode("forgot")} className="text-xs text-neutral-500 underline underline-offset-4 transition-colors duration-200 hover:text-[var(--wine)]" data-testid="forgot-password-link">Forgotten your password?</button>}
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="rounded-none bg-transparent" data-testid="auth-password-input" />
            </div>
            )}
            <Button type="submit" disabled={busy} className="w-full rounded-none py-6 text-base hover-lift" data-testid="auth-submit-button">
              {busy ? "One moment…" : mode === "login" ? "Sign in" : mode === "forgot" ? "Send reset link" : "Create account"}
            </Button>
            {mode !== "forgot" && (
              <>
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.16em] text-neutral-400"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
                <GoogleButton next={next || "/"} label={mode === "login" ? "Sign in with Google" : "Continue with Google"} className="py-4 hover-lift" testId="login-google-button" />
              </>
            )}
            {mode === "login" && config?.shopify_sso && (
              <>
                <Button asChild variant="outline" className="h-auto w-full whitespace-normal rounded-none px-4 py-4 text-center text-sm leading-snug hover-lift"><a href={shopifyStart} data-testid="shopify-sso-button"><ShoppingBag className="mr-2 h-4 w-4" /><span className="min-w-0 break-words">Sign in with your shop account</span></a></Button>
                <p className="text-center text-xs text-neutral-500">Same email you use on the shop — no second password.</p>
              </>
            )}
          </div>
          )}
          {ssoError && <p className="mt-4 border border-[var(--wine)] bg-[var(--wine)]/5 p-3 text-sm text-[var(--wine)]" data-testid="sso-error">{ssoError}</p>}
          <button type="button" onClick={() => (mode === "login" ? navigate(next ? `/signup?next=${encodeURIComponent(next)}` : "/signup") : (setMode("login"), setSent(false)))} className="mt-8 text-sm text-neutral-600 underline underline-offset-4 transition-colors duration-200 hover:text-[var(--wine)]" data-testid="auth-toggle-mode">
            {mode === "login" ? "New here? Open your cellar" : mode === "forgot" ? "Remembered it? Sign in" : "Already a member? Sign in"}
          </button>
          <div className="mt-12"><HowItWorksStrip /></div>
        </form>
      </div>
    </div>
  );
}
